// Browser File System Access API helpers: let a web app write files directly
// into a local folder the user picks (e.g. the Megasoft agent inbox).
// Supported in Chrome/Edge over HTTPS. The directory handle is persisted in IndexedDB.

const DB_NAME = 'megasoft_fs';
const STORE = 'handles';
const KEY = 'inbox_dir';

function openIdb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function isFsSupported() {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

export async function saveDirHandle(handle) {
  const db = await openIdb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(handle, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadDirHandle() {
  try {
    const db = await openIdb();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readonly');
      const g = tx.objectStore(STORE).get(KEY);
      g.onsuccess = () => resolve(g.result || null);
      g.onerror = () => resolve(null);
    });
  } catch (_e) {
    return null;
  }
}

export async function pickDir() {
  const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
  await saveDirHandle(handle);
  return handle;
}

export async function ensurePermission(handle) {
  if (!handle) return false;
  const opts = { mode: 'readwrite' };
  if ((await handle.queryPermission(opts)) === 'granted') return true;
  return (await handle.requestPermission(opts)) === 'granted';
}

// Write a file into the directory. Chrome's createWritable uses a swap file that
// is atomically committed on close(), so the agent won't read a partial file.
// After writing we read the file back to VERIFY it really exists; returns its size.
export async function writeFileToDir(dirHandle, name, contentStr) {
  const fh = await dirHandle.getFileHandle(name, { create: true });
  const writable = await fh.createWritable();
  await writable.write(contentStr);
  await writable.close();
  const check = await dirHandle.getFileHandle(name);
  const file = await check.getFile();
  return file.size;
}

// List file names in the directory (optionally filter by extension like '.json').
export async function listDir(dirHandle, ext) {
  const names = [];
  for await (const [name, handle] of dirHandle.entries()) {
    if (handle.kind === 'file' && (!ext || name.toLowerCase().endsWith(ext.toLowerCase()))) {
      names.push(name);
    }
  }
  return names;
}

// Write a small test file to verify the picked folder is writable and visible.
export async function writeTestFile(dirHandle) {
  const name = 'megasoft-test.txt';
  await writeFileToDir(dirHandle, name, 'Megasoft test ' + new Date().toISOString());
  return name;
}

// Trigger a browser download of a file (works in every browser; the user can set
// the browser's download folder to the Megasoft agent inbox).
export function downloadFile(name, contentStr) {
  const blob = new Blob([contentStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1500);
}
